/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Button, Modal, Text } from '@/shared/ui';

interface SettingsLeaveConfirmProps {
  opened: boolean;
  /** Keep the user where they are, with the draft intact. */
  onStay: () => void;
  /** Leave the tab and drop the draft. */
  onLeave: () => void;
}

/**
 * Asked when someone changes tabs with an unsaved draft.
 *
 * The prototype drops the draft silently on a tab change, which is fine in a
 * prototype and not fine in the product: the change the person made is gone
 * with no evidence that it ever existed, and the next time they look the old
 * value is simply back. So the discard becomes a question.
 *
 * @param props - Component props.
 * @returns The confirmation dialog.
 */
export function SettingsLeaveConfirm({ opened, onStay, onLeave }: SettingsLeaveConfirmProps) {
  const { t } = useTranslation('features/agency-settings');

  return (
    <Modal opened={opened} onClose={onStay} title={t('v2.leaveConfirm.title')} size="sm">
      <Text style={{ margin: 0, fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        {t('v2.leaveConfirm.body')}
      </Text>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)', marginTop: 'var(--space-5)' }}>
        <Button
          variant="outlined"
          size="sm"
          onClick={onStay}
          data-testid="settings-leave-stay"
          style={{ whiteSpace: 'nowrap' }}
        >
          {t('v2.leaveConfirm.stay')}
        </Button>
        <Button
          size="sm"
          onClick={onLeave}
          data-testid="settings-leave-discard"
          style={{ whiteSpace: 'nowrap' }}
        >
          {t('v2.leaveConfirm.leave')}
        </Button>
      </div>
    </Modal>
  );
}
